import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { Modal, Spinner } from 'react-bootstrap';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import { getMapData } from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import { getLocalizedItemLabels } from '../../utils/itemLocaleDisplay';
import { nearestProvinceName } from '../../utils/turkeyProvinceCenters';
import { normalizeMapItems } from '../../utils/mapItemCoordinates';

/** API'den gelen olası konum adı alanları (ilk dolu olan kullanılır) */
const resolveItemLocationLabel = (item) => {
  if (!item || typeof item !== 'object') return null;
  return (
    item.city ||
    item.locationName ||
    item.placeName ||
    item.mapTitle ||
    item.mapLabel ||
    item.location ||
    item.sehir ||
    item.konum ||
    (typeof item.place === 'string' ? item.place : null) ||
    null
  );
};

function getLocationDisplayTitle(loc, t) {
  if (!loc) return t('mapPage.locationFallback');
  if (loc.label) return loc.label;
  if (Array.isArray(loc.items)) {
    for (const it of loc.items) {
      const label = resolveItemLocationLabel(it);
      if (label) return label;
    }
  }
  const lat = Number.parseFloat(loc.lat);
  const lng = Number.parseFloat(loc.lng);
  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    const near = nearestProvinceName(lat, lng);
    if (near) return near;
    return t('mapPage.locationCoords')
      .replace('{lat}', lat.toFixed(2))
      .replace('{lng}', lng.toFixed(2));
  }
  return t('mapPage.locationFallback');
}

function countUniqueAuthors(items) {
  return new Set((items || []).map((item) => item.writerName).filter(Boolean)).size;
}

// Leaflet icon fix for React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/** Leaflet, yüzde yükseklikte ilk ölçümde 0px kalabiliyor; haritayı yeniden boyutlandır */
function MapResize() {
  const map = useMap();
  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
}

const MAP_HEIGHT = 'calc(100vh - 120px)';

const Map = () => {
  const { t, locale } = useLanguage();
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    setMapReady(true);
  }, []);

  const turkeyCenter = [39.0, 35.0];
  const defaultZoom = 6;

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getMapData();

        if (response.status && response.data) {
          const groupedData = {};

          normalizeMapItems(response.data).forEach((item) => {
            const coordKey = `${item.lat},${item.lng}`;

            if (!groupedData[coordKey]) {
              groupedData[coordKey] = {
                lat: item.lat,
                lng: item.lng,
                label: resolveItemLocationLabel(item),
                items: [],
              };
            } else {
              const nextLabel = resolveItemLocationLabel(item);
              if (nextLabel && !groupedData[coordKey].label) {
                groupedData[coordKey].label = nextLabel;
              }
            }

            groupedData[coordKey].items.push(item);
          });

          setMapData(Object.values(groupedData));
        } else {
          setError(t('mapPage.errorLoad'));
        }
      } catch (err) {
        console.error('Map data fetch error:', err);
        setError(t('mapPage.errorFetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = (itemId) => {
    window.open(`/article/${itemId}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="map-container">
        <div className="map-loading" role="status" aria-live="polite">
          <Spinner animation="border" aria-hidden="true" />
          <p>{t('mapPage.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-container">
        <div className="map-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const articleCount = selectedLocation?.items.length ?? 0;
  const authorCount = countUniqueAuthors(selectedLocation?.items);

  return (
    <div className="map-container">
      <div className="map-wrapper">
        {mapReady ? (
          <MapContainer
            center={turkeyCenter}
            zoom={defaultZoom}
            className="leaflet-map"
            style={{ height: MAP_HEIGHT, width: '100%', minHeight: '480px' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapResize />

            {mapData.map((location) => (
              <Marker
                key={`${location.lat}-${location.lng}`}
                position={[parseFloat(location.lat), parseFloat(location.lng)]}
                eventHandlers={{
                  click: () => {
                    setSelectedLocation(location);
                    setShowModal(true);
                  },
                }}
              />
            ))}
          </MapContainer>
        ) : null}
      </div>

      {mapData.length === 0 && (
        <div className="map-no-data">
          <p>{t('mapPage.noData')}</p>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="map-modal-header">
          <Modal.Title>
            {getLocationDisplayTitle(selectedLocation, t)}
            <div className="map-modal-stats">
              {t('mapPage.stats')
                .replace('{articles}', String(articleCount))
                .replace('{authors}', String(authorCount))}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="map-modal-body">
          {selectedLocation?.items.map((item, idx) => {
            const labels = getLocalizedItemLabels(item, locale);
            return (
              <div
                key={item.id}
                className="modal-item-card"
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(item.id);
                  }
                }}
              >
                <div className="modal-item-card__index">{idx + 1}</div>
                <div className="modal-item-card__content">
                  <h6 className="modal-item-card__title">{labels.title}</h6>
                  {item.writerName ? (
                    <div className="modal-item-card__author">{item.writerName}</div>
                  ) : null}
                </div>
                <div className="modal-item-card__arrow" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6-6 6-1.41-1.41z" />
                  </svg>
                </div>
              </div>
            );
          })}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Map;
